import { useState } from "react";
import { Link } from "react-router-dom";
import { Car, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useLanguage } from "@/context/LanguageContext";
import { mediaUrl } from "@/context/SettingsContext";
import { priceLabel, fmtKm } from "./vehicleUtils";
import { VehicleInterestForm } from "./VehicleInterestForm";
import { VehicleShare } from "./VehicleShare";

const Spec = ({ label, value }) => value ? <div className="rounded-xl bg-mint px-3 py-2"><p className="font-mono text-[9px] uppercase tracking-widest text-brand">{label}</p><p className="text-sm font-semibold text-forest">{value}</p></div> : null;

export const VehicleSpecs = ({ v }) => {
  const { t } = useLanguage();
  const tv = t.vehicles;
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      <Spec label={tv.sell.year} value={v.year} />
      <Spec label={tv.sell.mileage} value={v.mileage_km != null && v.mileage_km !== "" ? fmtKm(v.mileage_km) : ""} />
      <Spec label={tv.sell.fuel} value={tv.fuel[v.fuel] || v.fuel} />
      <Spec label={tv.sell.gearbox} value={tv.gearbox[v.gearbox] || v.gearbox} />
      <Spec label={tv.color} value={v.color} />
    </div>
  );
};

export const VehicleGallery = ({ v, testid = "vehicle-dialog" }) => {
  const [photo, setPhoto] = useState(0);
  return (
    <>
      <div className="aspect-[4/3] overflow-hidden rounded-2xl bg-white">
        {v.photos?.length ? <img src={mediaUrl(v.photos[photo])} alt={v.title} className="h-full w-full object-cover" data-testid={`${testid}-photo`} /> : <div className="flex h-full items-center justify-center text-brand/40"><Car className="h-20 w-20" /></div>}
      </div>
      {v.photos?.length > 1 && (
        <div className="mt-3 flex gap-2 overflow-x-auto">
          {v.photos.map((p, i) => <button key={p} type="button" onClick={() => setPhoto(i)} data-testid={`${testid}-thumb-${i}`} className={`h-14 w-20 shrink-0 overflow-hidden rounded-lg border-2 ${i === photo ? "border-gold" : "border-transparent"}`}><img src={mediaUrl(p)} alt="" className="h-full w-full object-cover" /></button>)}
        </div>
      )}
    </>
  );
};

export const VehicleDialog = ({ vehicle: v, onClose }) => {
  const { t } = useLanguage();
  const tv = t.vehicles;
  if (!v) return null;
  return (
    <Dialog open={!!v} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto rounded-3xl p-0" data-testid="vehicle-dialog">
        <div className="grid md:grid-cols-2">
          <div className="bg-mint p-4"><VehicleGallery v={v} /></div>
          <div className="p-6 sm:p-8">
            <DialogHeader className="text-left">
              <span className={`w-fit rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white ${v.kind === "rent" ? "bg-brand" : "bg-gold"}`}>{tv.kind[v.kind]}</span>
              <DialogTitle className="font-display text-2xl font-extrabold text-forest" data-testid="vehicle-dialog-title">{v.title}</DialogTitle>
              <DialogDescription className="font-display text-xl font-extrabold text-gold" data-testid="vehicle-dialog-price">{priceLabel(v, tv)}</DialogDescription>
            </DialogHeader>
            <div className="mt-4"><VehicleSpecs v={v} /></div>
            {v.description && <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-brand-hover" data-testid="vehicle-dialog-description">{v.description}</p>}
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <VehicleShare v={v} prefix="vehicle-dialog-share" />
              <Link to={`/vehicules/${v.id}`} onClick={onClose} data-testid="vehicle-dialog-open-page" className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-gold hover:text-gold-hover">{tv.openPage}<ExternalLink className="h-3.5 w-3.5" /></Link>
            </div>
            <div className="mt-5"><VehicleInterestForm v={v} /></div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
